import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../component/header";
import Footer from "../component/Footer";
import BannerImg from "../image/gallaryBanner.png";
import { ArrowLeft, ChevronRight, Images, Lock } from "lucide-react";

const API_BASE_URL = "";

function imageUrl(path) {
  if (!path) return BannerImg;
  return path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
}

function GalleryDetail() {
  const { gallerySlug = "gallery", galleryId } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function fetchGallery() {
      try {
        setLoading(true);
        setError("");
        const response = await fetch(`/api/gallery/${galleryId}`);
        const result = await response.json();
        if (!result.success) throw new Error(result.message || "Gallery not found.");
        if (isMounted) setGallery(result.data);
      } catch (err) {
        if (isMounted) setError(err.message || "Unable to load gallery.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchGallery();
    return () => {
      isMounted = false;
    };
  }, [galleryId]);

  const groups = useMemo(() => {
    if (!gallery) return [];
    const activeGroups = (gallery.groups || []).filter((group) => group.isActive !== false);
    if (activeGroups.length > 0) return activeGroups;
    if ((gallery.images || []).length > 0) {
      return [
        {
          _id: "default",
          title: gallery.title,
          description: gallery.description,
          coverImage: gallery.coverImage,
          images: gallery.images,
        },
      ];
    }
    return [];
  }, [gallery]);

  return (
    <div className="min-h-screen bg-[#f7f8f8] text-left">
      <Header />
      <main>
        {loading && (
          <>
            <section className="px-4 pb-16 pt-32 text-white bg-[#111718]">
          <div className="mx-auto max-w-7xl">
                <div className="mb-8 h-4 w-72 max-w-full animate-pulse rounded-full bg-white/15" />
                <div className="mb-6 h-10 w-48 animate-pulse rounded-full bg-white/15" />
                <div className="h-3 w-44 animate-pulse rounded-full bg-[#00F0FB]/35" />
                <div className="mt-5 h-16 max-w-3xl animate-pulse rounded-lg bg-white/15" />
                <div className="mt-5 h-20 max-w-2xl animate-pulse rounded-lg bg-white/10" />
              </div>
            </section>
            <section className="mx-auto max-w-7xl px-4 py-10">
              <div className="mb-6 flex items-end justify-between gap-4">
                <div>
                  <div className="h-4 w-48 animate-pulse rounded-full bg-[#dce5e5]" />
                  <div className="mt-3 h-8 w-56 animate-pulse rounded-full bg-[#cfdada]" />
                </div>
                <div className="h-4 w-20 animate-pulse rounded-full bg-[#dce5e5]" />
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {[0, 1, 2].map((item) => (
                  <div key={item} className="overflow-hidden rounded-lg border border-[#dce5e5] bg-white shadow-sm">
                    <div className="h-64 animate-pulse bg-gradient-to-br from-[#dfe7e7] via-white to-[#d8ecee]" />
                    <div className="p-4">
                      <div className="h-4 w-full animate-pulse rounded-full bg-[#e3eaea]" />
                      <div className="mt-3 h-4 w-2/3 animate-pulse rounded-full bg-[#e3eaea]" />
                      <div className="mt-5 h-3 w-24 animate-pulse rounded-full bg-[#bdeff5]" />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {!loading && error && (
          <div className="px-4 pb-16 pt-32">
            <div className="mx-auto max-w-5xl rounded-lg border border-[#dce5e5] bg-white p-8 font-outfit text-[#3b4546] shadow-sm">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#E8FBFD] text-[#008EA8]">
                <Lock size={24} />
              </div>
              <h1 className="font-cinzel text-3xl text-[#111718]">Membership access required</h1>
              <p className="mt-3 max-w-2xl leading-7">
                {error} Upgrade to reveal the image collections, images, and commentary attached to this collection.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/membership")}
                  className="rounded-full bg-[#111718] px-5 py-3 text-sm font-bold uppercase text-white transition hover:bg-[#00A6C8]"
                >
                  View membership options
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/gallery")}
                  className="rounded-full border border-[#cfdada] px-5 py-3 text-sm font-bold uppercase text-[#111718] transition hover:border-[#00A6C8]"
                >
                  Back to gallery
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && gallery && (
          <>
            <section
              className="relative px-4 pb-16 pt-32 text-white"
              style={{
                backgroundImage: `linear-gradient(90deg, rgba(8, 15, 16, 0.88), rgba(8, 15, 16, 0.46)), url(${imageUrl(gallery.coverImage)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="mx-auto max-w-7xl">
                <nav className="mb-8 flex flex-wrap items-center gap-2 font-outfit text-sm text-white/75">
                  <Link to="/" className="hover:text-[#00F0FB]">Home</Link>
                  <ChevronRight size={15} />
                  <Link to="/gallery" className="hover:text-[#00F0FB]">Gallery</Link>
                  <ChevronRight size={15} />
                  <span className="text-white">{gallery.title}</span>
                </nav>

                <button
                  type="button"
                  onClick={() => navigate("/gallery")}
                  className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 font-outfit text-sm font-semibold text-white hover:border-[#00F0FB] hover:text-[#00F0FB]"
                >
                  <ArrowLeft size={16} />
                  Back to collections
                </button>
                <p className="font-outfit text-xs uppercase tracking-[0.24em] text-[#00F0FB]">Album Collections</p>
                <h1 className="mt-4 max-w-4xl font-cinzel text-4xl leading-tight md:text-6xl">
                  {gallery.title}
                </h1>
                <p className="mt-5 max-w-3xl font-outfit text-base leading-relaxed text-white/85 md:text-lg">
                  {gallery.description || "Choose an album to view the published images and commentary."}
                </p>
              </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 py-10">
              <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="font-outfit text-sm text-[#687475]">Albums inside this collection.</p>
                  <h2 className="font-cinzel text-3xl text-[#111718]">Select an Album</h2>
                </div>
                <p className="font-outfit text-sm text-[#687475]">{groups.length} albums</p>
              </div>

              {groups.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[#c9d2d2] bg-white p-10 text-center">
                  <Images className="mx-auto text-[#00A6C8]" size={34} />
                  <h3 className="mt-4 font-cinzel text-2xl text-[#111718]">No albums published yet</h3>
                  <p className="mt-2 font-outfit text-[#687475]">Albums added by admin will appear here.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  {groups.map((group) => (
                    <Link
                      key={group._id}
                      to={`/gallery/${gallerySlug}/${gallery._id}/${group._id}`}
                      className="group overflow-hidden rounded-lg border border-[#dce5e5] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
                    >
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={imageUrl(group.coverImage)}
                          alt={group.title}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <span className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
                        <span className="absolute bottom-4 left-4 right-4 font-cinzel text-2xl leading-tight text-white">
                          {group.title}
                        </span>
                      </div>
                      <div className="p-4">
                        <p className="font-outfit text-sm italic text-[#3b4546]">
                          {group.description || "View images and published commentary."}
                        </p>
                        <p className="mt-3 font-outfit text-xs font-semibold uppercase tracking-[0.14em] text-[#00A6C8]">
                          {group.images?.length || 0} images
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default GalleryDetail;
